import { useCallback, useRef, useState } from 'react';
/**
 * @typedef {import('react').SyntheticEvent} Event
 */

const pluralHooks = ['useMultipleInputValues'];
const validateProps = (_ref, _ref2, hookName) => {
  let {
    value,
    onChange,
    readOnly,
    initialValue
  } = _ref;
  let {
    isCurrentlyControlled,
    isControlled
  } = _ref2;
  const s = pluralHooks.includes(hookName) ? 's' : '';
  const usageError = error => {
    // Errors inside hooks in React Native get incomplete stack traces pointing at parent component only.
    // Help devs out by telling them exactly which hook threw the error as well as why.
    throw new Error(`${hookName} ${error}.

Consumers of this hook must be one of:
1. An "uncontrolled" component responding directly to user actions, with an optional \`initialValue${s}\` but no \`value${s}\` prop,
2. A "controlled" component where an always-defined \`value${s}\` prop is managed by an \`onChange\` handler, with no \`initialValue${s}\`,
3. A "read-only" component, with \`readOnly\` prop set as \`true\`.
`);
  };
  if (value && !onChange && !readOnly) {
    usageError(`has \`value${s}\` prop without \`onChange\` or \`readOnly\``);
  }
  if (initialValue && value) {
    usageError(`has both \`initialValue${s}\` and \`value${s}\``);
  }
  if (isControlled && !isCurrentlyControlled) {
    usageError(`stopped receiving \`value${s}\` from parent after delegating state management`);
  }
  if (!isControlled && isCurrentlyControlled) {
    usageError(`started receiving \`value${s}\` from parent after starting managing own state`);
  }
};

/**
 * Hook used for controlling the input state of input components that have a singular value.
 * If `value` prop is passed in, this delegates state management to a parent using a passed-in `onChange` prop.
 * If `value` prop is undefined, the hook manages its own state.
 *
 * TODO: when implementing full UDS forms, integrate validation support etc and test alongside common forms
 * management tools such as Formik and React Hooks Form.
 *
 * @param {object} props
 * @param {string|number|null} [props.value] - for a controlled input, the active values as set by a parent
 * @param {string|number} [props.initialValue] - for an uncontrolled input, the default active values
 * @param {function} [props.onChange] - function called when values change (required for controlled inputs)
 * @param {boolean} [props.readOnly] - if true, stops the input values from changing
 *
 * @param {string} hookName - optional, used for tailoring error messages
 *
 * @returns {{
 *   currentValue: string|number|null
 *   setValue: (newValue: string|number|null|(oldValue: string|number) => string|number, event: Event) => void
 *   resetValue: () => void
 *   isControlled: bool
 * }}
 */
export const useInputValue = function () {
  let props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let hookName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'useInputValue';
  const isCurrentlyControlled = props.value !== undefined;
  const [isControlled] = useState(isCurrentlyControlled);
  validateProps(props, {
    isControlled,
    isCurrentlyControlled
  }, hookName);
  const {
    value,
    initialValue,
    inputRef,
    onChange,
    readOnly = false
  } = props;
  const [ownValue, setOwnValue] = useState(!isControlled && initialValue);
  const currentValue = isControlled ? value : ownValue;

  // Store the first valid value, for resetting input to default. If behaviour like Formik's `enableReinitialize`
  // is needed, add useEffect calling a set fn if !deepEqual(initializedValues, initialValues)
  const valueRef = useRef({
    initial: currentValue,
    value: currentValue
  });
  // Make current value accessible inside useCallback without rememoizing every time the value changes
  valueRef.current.value = currentValue;
  const isDirty = currentValue !== valueRef.current.initial;
  const setValue = useCallback((arg, event) => {
    if (readOnly) return;
    const newValue = typeof arg === 'function' ? arg(valueRef.current.value) : arg;
    if (!isControlled) {
      setOwnValue(newValue);
      if (inputRef?.current) inputRef.current.value = newValue ?? '';
    }
    // Call onChange handler if there's something for it to handle (event or a changed value)
    if (onChange && (event || valueRef.current.value !== newValue)) onChange(newValue, event);
  }, [inputRef, isControlled, onChange, readOnly]);
  const resetValue = useCallback(event => setValue(valueRef.current.initial, event), [setValue]);
  return {
    currentValue,
    setValue,
    resetValue,
    isControlled,
    isDirty
  };
};

/**
 * Hook used for controlling the input state of input components that have multiple named on/off states.
 * If `values` are passed in, this delegates state management to a parent using a passed-in `onChange` prop.
 * If `values` prop is not passed in, the hook manages its own state.
 *
 * TODO: when implementing full UDS forms, integrate validation support etc and test alongside common forms
 * management tools such as Formik and React Hooks Form.
 *
 * @param {object} props
 * @param {string[]|number[]} [props.values] - for a controlled input, the active values as set by a parent
 * @param {string[]|number[]} [props.initialValues] - for an uncontrolled input, the default active values
 * @param {number?} [props.maxValues] - maximum number of values that may be selected at once (no limit if null)
 * @param {function} [props.onChange] - function called when values change (required for controlled inputs)
 * @param {boolean} [props.readOnly] - if true, stops the input values from changing
 *
 * @param {string} componentName - optional, used in error messages
 *
 * @returns {{
 *   currentValues: any
 *   resetValues: () => void
 *   setValues: (newValues: string[]|number[]|(oldValues: string[]|number[]) => string[]|number[], event: Event) => void
 *   toggleOneValue: (value: string|number) => void
 *   unsetValues: () => void
 * }}
 */
export const useMultipleInputValues = function () {
  let {
    values,
    initialValues,
    maxValues,
    onChange,
    readOnly
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    currentValue,
    setValue,
    resetValue
  } = useInputValue({
    readOnly,
    onChange,
    value: values,
    // if we're controlling our own state, always start with an array
    initialValue: initialValues ?? (values === undefined ? [] : undefined)
  }, 'useMultipleInputValues');
  const enforceMaxValues = useCallback(newValues => {
    if (!maxValues) return newValues;
    const excess = newValues.length - maxValues;
    return excess > 0 ? newValues.slice(excess) : newValues;
  }, [maxValues]);
  const currentValues = enforceMaxValues(currentValue);
  const setValues = useCallback((newValues, event) => {
    const validNewValues = enforceMaxValues(newValues);
    setValue(validNewValues, event);
  }, [setValue, enforceMaxValues]);
  const resetValues = resetValue;
  const unsetValues = useCallback(event => setValues([], event), [setValues]);
  const toggleOneValue = useCallback((newValue, event) => {
    if (readOnly) return;
    // This will only work with primitive values (e.g. strings, numbers), swap for .some() and
    // a deepEqual() function if there's any use case for toggling stored objects or arrays.
    const newValues = currentValues.includes(newValue) ? currentValues.filter(oldValue => oldValue !== newValue) : [...currentValues, newValue];
    setValues(newValues, event);
  }, [currentValues, readOnly, setValues]);
  return {
    currentValues,
    resetValues,
    setValues,
    toggleOneValue,
    unsetValues
  };
};